import axios, { rawAxios } from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type { StatementCandidateFilters, StatementSelection } from './statements.schema'
import type {
  CustomerStatementRun,
  CustomerStatementRunSummary,
  LengthAwarePage,
  StatementCandidates,
} from './statements.types'

interface StatementScope {
  organizationId: OrganizationId
}

/** A request about one run. */
interface StatementRunRequest extends StatementScope {
  runId: number
}

/** The key entries each fetcher reads; `statementQueries` sets them. */
export interface StatementCandidatesKeyEntries extends StatementScope {
  filters?: StatementCandidateFilters | undefined
}
export interface StatementRunsKeyEntries extends StatementScope {
  page?: number | undefined
}
export interface StatementRunKeyEntries extends StatementScope {
  runId?: number | undefined
}

/** Fetchers read only the query key. */
type StatementQueryContext<TEntries> = Pick<QueryFunctionContext<[TEntries]>, 'queryKey'>

/** `CustomerStatementRunRequest` for prepare: the candidate filters, the selection and a key. */
export type StatementRunPrepareRequest = StatementCandidateFilters &
  StatementSelection & { idempotency_key: string }

/** The POST actions on a prepared run. */
export type StatementRunAction = 'dispatch' | 'retry' | 'printed'

export function getStatementCandidates({
  queryKey: [{ organizationId, filters }],
}: StatementQueryContext<StatementCandidatesKeyEntries>) {
  return axios.get<StatementCandidates>(
    '/customer-statement-runs/candidates',
    buildAdminRequestConfig({ organizationId, params: { ...filters } })
  )
}
export function getStatementRuns({
  queryKey: [{ organizationId, page = 1 }],
}: StatementQueryContext<StatementRunsKeyEntries>) {
  return axios.get<LengthAwarePage<CustomerStatementRunSummary>>(
    '/customer-statement-runs',
    buildAdminRequestConfig({ organizationId, params: { page } })
  )
}
export function getStatementRun({
  queryKey: [{ organizationId, runId }],
}: StatementQueryContext<StatementRunKeyEntries>) {
  return axios.get<CustomerStatementRun>(
    `/customer-statement-runs/${runId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
export function prepareStatementRun({
  organizationId,
  data,
}: StatementScope & { data: StatementRunPrepareRequest }) {
  return axios.post<CustomerStatementRun>(
    '/customer-statement-runs/prepare',
    data,
    buildAdminRequestConfig({ organizationId })
  )
}
export function actOnStatementRun({
  organizationId,
  runId,
  action,
}: StatementRunRequest & { action: StatementRunAction }) {
  return axios.post<CustomerStatementRun>(
    `/customer-statement-runs/${runId}/${action}`,
    {},
    buildAdminRequestConfig({ organizationId })
  )
}
export function previewStatementRun({ organizationId, runId }: StatementRunRequest) {
  return axios.get<string>(
    `/customer-statement-runs/${runId}/preview`,
    buildAdminRequestConfig({ organizationId, responseType: 'text' })
  )
}
export function downloadStatementRun({ organizationId, runId }: StatementRunRequest) {
  return rawAxios.get<Blob>(
    `/customer-statement-runs/${runId}/download`,
    buildAdminRequestConfig({ organizationId, responseType: 'blob' })
  )
}
